import fallbackZones from '../constants/ianaTimeZones.generated.json';

/**
 * Full IANA time zone identifiers for the Profile selector.
 * Prefer `Intl.supportedValuesOf('timeZone')` when the runtime provides it;
 * otherwise use the generated full list (not a curated subset).
 */
export function listIanaTimeZones(): string[] {
  const intlWithSupported = Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  };
  if (typeof intlWithSupported.supportedValuesOf === 'function') {
    try {
      return [...intlWithSupported.supportedValuesOf('timeZone')].sort((a, b) =>
        a.localeCompare(b),
      );
    } catch {
      // Fall through to the bundled full list.
    }
  }
  return [...(fallbackZones as string[])].sort((a, b) => a.localeCompare(b));
}

export interface IanaTimeZoneOption {
  id: string;
  /** `(UTC±HH:MM) Area/City`, sorted by current offset in the picker. */
  label: string;
  offsetMinutes: number;
}

/**
 * Minutes east of UTC for `timeZone` at `at` (negative west of UTC).
 * Null when this runtime rejects the name. Android's ICU throws
 * `Invalid timezone name` for some IANA ids, and for `hourCycle`, even when
 * `supportedValuesOf` listed the id.
 */
export function timeZoneOffsetMinutes(
  timeZone: string,
  at: Date = new Date(),
): number | null {
  const mapped = readZonedParts(timeZone, at);
  if (!mapped) {
    return null;
  }
  const asUtc = zonedPartsAsUtc(mapped, Number(mapped.hour));
  if (!Number.isFinite(asUtc)) {
    return null;
  }
  if (mapped.hour !== '24') {
    return Math.round((asUtc - at.getTime()) / 60000);
  }
  // hourCycle h23 uses 24 for the instant at the end of the civil day.
  // hour12: false uses 24 for the whole local midnight hour on that same day.
  // Keep whichever reading sits closer to the instant. A tie is that midnight.
  const midnightUtc = zonedPartsAsUtc(mapped, 0);
  if (!Number.isFinite(midnightUtc)) {
    return null;
  }
  const chosen =
    Math.abs(midnightUtc - at.getTime()) <= Math.abs(asUtc - at.getTime())
      ? midnightUtc
      : asUtc;
  return Math.round((chosen - at.getTime()) / 60000);
}

function zonedPartsAsUtc(mapped: Record<string, string>, hour: number): number {
  return Date.UTC(
    Number(mapped.year),
    Number(mapped.month) - 1,
    Number(mapped.day),
    hour,
    Number(mapped.minute),
    Number(mapped.second),
  );
}

type ZonedHourStyle = 'hourCycle' | 'hour12';

/** First style that returned a full set of parts. Later zones reuse it. */
let preferredHourStyle: ZonedHourStyle | undefined;

function zonedFieldOptions(
  timeZone: string,
  style: ZonedHourStyle,
): Intl.DateTimeFormatOptions {
  return {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    ...(style === 'hourCycle'
      ? { hourCycle: 'h23' as const }
      : { hour12: false as const }),
  };
}

function hourStyleOrder(): ZonedHourStyle[] {
  if (preferredHourStyle === 'hour12') {
    return ['hour12', 'hourCycle'];
  }
  return ['hourCycle', 'hour12'];
}

function readZonedParts(
  timeZone: string,
  at: Date,
): Record<string, string> | null {
  for (const style of hourStyleOrder()) {
    try {
      const parts = new Intl.DateTimeFormat(
        'en-US',
        zonedFieldOptions(timeZone, style),
      ).formatToParts(at);
      const mapped: Record<string, string> = {};
      for (const part of parts) {
        if (part.type !== 'literal') {
          mapped[part.type] = part.value;
        }
      }
      const complete =
        mapped.year != null &&
        mapped.year !== '' &&
        mapped.month != null &&
        mapped.month !== '' &&
        mapped.day != null &&
        mapped.day !== '' &&
        mapped.hour != null &&
        mapped.hour !== '' &&
        mapped.minute != null &&
        mapped.minute !== '' &&
        mapped.second != null &&
        mapped.second !== '';
      if (!complete) {
        continue;
      }
      preferredHourStyle = style;
      return mapped;
    } catch {
      // Rejected zone name, or an option this ICU build does not support.
    }
  }
  return null;
}

/** `(UTC-05:00)` style label for the current offset, including `UTC+00:00`. */
export function formatUtcOffset(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const minutes = String(abs % 60).padStart(2, '0');
  return `(UTC${sign}${hours}:${minutes})`;
}

export function formatTimeZoneOptionLabel(
  timeZone: string,
  at: Date = new Date(),
): string {
  const offsetMinutes = timeZoneOffsetMinutes(timeZone, at);
  if (offsetMinutes == null) {
    return timeZone;
  }
  return `${formatUtcOffset(offsetMinutes)} ${timeZone}`;
}

/** Full IANA list as select rows, ordered by current UTC offset then name. */
export function listIanaTimeZoneOptions(
  at: Date = new Date(),
): IanaTimeZoneOption[] {
  return listIanaTimeZones()
    .flatMap(id => {
      const offsetMinutes = timeZoneOffsetMinutes(id, at);
      if (offsetMinutes == null) {
        return [];
      }
      return [
        {
          id,
          offsetMinutes,
          label: `${formatUtcOffset(offsetMinutes)} ${id}`,
        },
      ];
    })
    .sort(
      (a, b) => a.offsetMinutes - b.offsetMinutes || a.id.localeCompare(b.id),
    );
}
